import { clip, redactText } from '../../shared/redact';
import type { AreaRect } from './AreaPicker';

/*
 * Report 107, item 4 (Quint shot_blocks, Жс1–Жс2) and report 287 (Quint manual_shot, Сн3): which blocks of the page a shot try
 * had in its frame. A block is an element with `data-bug-block` (the same marks the record and the UX session use) or a
 * meaningful node of the page tree — `main`, `nav`, `aside`, `header`, `section`, `form`, `table`, `dialog` and the
 * landmark roles — so a site with no marks (the tracker) still gets its list. A block is in the list when its rectangle
 * crosses the frame — for a shot of the whole page, the window (less the strip the panel took) — as the layout is at the
 * moment the shot starts. The widget's own windows are not blocks of the page. Names: the list goes through `maskKnown`
 * with the rest of the log when it is sent, and before it is shown to the person.
 */

/** One block in the frame: its mark and the path of the element (tag, id, two classes, up to the nearest block or main). */
export interface ShotBlock {
  block: string;
  path: string;
}

/** Nodes of the page tree that count as blocks when the site marks none (Сн3). */
const TREE_BLOCKS = [
  'main',
  'nav',
  'aside',
  'header',
  'footer',
  'section',
  'form',
  'table',
  'dialog',
  '[role="main"]',
  '[role="navigation"]',
  '[role="complementary"]',
  '[role="region"]',
  '[role="tablist"]',
  '[role="toolbar"]',
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[role="table"]',
  '[role="grid"]',
  '[role="form"]',
].join(', ');
const BLOCK_SELECTOR = `[data-bug-block], ${TREE_BLOCKS}`;

/** A page is not likely to have more marked blocks in view; a cap keeps one entry small. */
export const SHOT_BLOCKS_MAX = 30;

/** The name of a block: the site's mark, else its aria label, its first heading, or the tag with a class. */
const labelOf = (el: Element): string => {
  const mark = el.getAttribute('data-bug-block');
  if (mark) return mark;
  const aria = el.getAttribute('aria-label');
  if (aria) return aria;
  const by = el.getAttribute('aria-labelledby');
  const named = by ? document.getElementById(by.split(/\s+/)[0] ?? '')?.textContent?.trim() : '';
  if (named) return named;
  const heading = el.querySelector('h1, h2, h3, h4, caption, legend')?.textContent?.trim();
  if (heading) return heading;
  const cls = [...el.classList][0];
  return `${el.tagName.toLowerCase()}${cls ? `.${cls}` : ''}`;
};

const blockPath = (el: Element): string => {
  const parts: string[] = [];
  let node: Element | null = el;
  while (node && node !== document.body && parts.length < 5) {
    const cls = [...node.classList].slice(0, 2).map((c) => `.${c}`).join('');
    parts.unshift(`${node.tagName.toLowerCase()}${node.id ? `#${node.id}` : ''}${cls}`);
    if (node.tagName === 'MAIN') break;
    node = node.parentElement;
    if (node?.hasAttribute('data-bug-block')) break;
  }
  return parts.join(' > ');
};

/** Blocks whose rectangles cross `area` (viewport coordinates), or the window less `cropRight` when there is no frame. */
export function blocksInView(area?: AreaRect, cropRight = 0): ShotBlock[] {
  const left = area ? area.x : 0;
  const top = area ? area.y : 0;
  const right = area ? area.x + area.w : window.innerWidth - cropRight;
  const bottom = area ? area.y + area.h : window.innerHeight;
  const found: ShotBlock[] = [];
  for (const el of document.querySelectorAll(BLOCK_SELECTOR)) {
    if (el.closest('.jaiora-bugs, [data-bug-own]')) continue;
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    if (r.left >= right || r.right <= left || r.top >= bottom || r.bottom <= top) continue;
    found.push({
      block: clip(redactText(labelOf(el).replace(/\s+/g, ' ')), 80),
      path: clip(redactText(blockPath(el)), 160),
    });
    if (found.length >= SHOT_BLOCKS_MAX) break;
  }
  return found;
}

/** «Затронуто: …» — the blocks a shot touched in a line for the person: each name once, a few, then «ещё N» (Сн3). */
export function touchedText(blocks: ShotBlock[], show: (text: string) => string = (text) => text, max = 4): string {
  const names = [...new Set(blocks.map((b) => show(b.block).trim()).filter(Boolean))];
  if (names.length === 0) return '';
  return names.length > max ? `${names.slice(0, max).join(', ')} и ещё ${names.length - max}` : names.join(', ');
}
